/**
 * OffshoreStaffModal — Add / Edit offshore staff compliance record
 *
 * Schema fields (from schema-compliance.ts / compliance-audits.ts):
 *  - staffName, staffRole, staffEmail
 *  - providerCompany (NOT agencyName), providerCountry (NOT location)
 *  - accessLevel (required)
 *  - complianceStatus: compliant | non_compliant | partially_compliant | not_applicable
 *  - confidentialitySigned (NOT deviceCompliant), confidentialityDate
 *  - privacyTrainingCompleted (NOT trainingCompliant), privacyTrainingDate
 *  - startDate (required), endDate
 *  - NO isActive boolean — active = endDate IS NULL or future
 *  - NO deviceCompliant / accessCompliant / trainingCompliant
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OffshoreStaffData {
  id?: string;
  staffName?: string;
  staffRole?: string | null;
  staffEmail?: string | null;
  providerCompany?: string;
  providerCountry?: string;
  accessLevel?: string;
  complianceStatus?: string;
  confidentialitySigned?: boolean | null;
  confidentialityDate?: string | null;
  privacyTrainingCompleted?: boolean | null;
  privacyTrainingDate?: string | null;
  startDate?: string;
  endDate?: string | null;
  notes?: string | null;
}

interface OffshoreStaffModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingStaff?: OffshoreStaffData | null;
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMPLIANCE_STATUSES = [
  { value: "compliant", label: "Compliant" },
  { value: "partially_compliant", label: "Partially Compliant" },
  { value: "non_compliant", label: "Non-Compliant" },
  { value: "not_applicable", label: "Not Applicable" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OffshoreStaffModal({
  open,
  onOpenChange,
  editingStaff,
  onSuccess,
}: OffshoreStaffModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!editingStaff?.id;

  // Personal
  const [staffName, setStaffName] = useState("");
  const [staffRole, setStaffRole] = useState("");
  const [staffEmail, setStaffEmail] = useState("");
  const [providerCompany, setProviderCompany] = useState("");
  const [providerCountry, setProviderCountry] = useState("");
  const [accessLevel, setAccessLevel] = useState("");
  const [complianceStatus, setComplianceStatus] = useState("compliant");

  // Compliance
  const [confidentialitySigned, setConfidentialitySigned] = useState(false);
  const [confidentialityDate, setConfidentialityDate] = useState("");
  const [privacyTrainingCompleted, setPrivacyTrainingCompleted] = useState(false);
  const [privacyTrainingDate, setPrivacyTrainingDate] = useState("");

  // Dates
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open && editingStaff) {
      setStaffName(editingStaff.staffName ?? "");
      setStaffRole(editingStaff.staffRole ?? "");
      setStaffEmail(editingStaff.staffEmail ?? "");
      setProviderCompany(editingStaff.providerCompany ?? "");
      setProviderCountry(editingStaff.providerCountry ?? "");
      setAccessLevel(editingStaff.accessLevel ?? "");
      setComplianceStatus(editingStaff.complianceStatus ?? "compliant");
      setConfidentialitySigned(editingStaff.confidentialitySigned ?? false);
      setConfidentialityDate(editingStaff.confidentialityDate ?? "");
      setPrivacyTrainingCompleted(editingStaff.privacyTrainingCompleted ?? false);
      setPrivacyTrainingDate(editingStaff.privacyTrainingDate ?? "");
      setStartDate(editingStaff.startDate ?? "");
      setEndDate(editingStaff.endDate ?? "");
      setNotes(editingStaff.notes ?? "");
    } else if (open && !editingStaff) {
      setStaffName("");
      setStaffRole("");
      setStaffEmail("");
      setProviderCompany("");
      setProviderCountry("");
      setAccessLevel("");
      setComplianceStatus("compliant");
      setConfidentialitySigned(false);
      setConfidentialityDate("");
      setPrivacyTrainingCompleted(false);
      setPrivacyTrainingDate("");
      setStartDate("");
      setEndDate("");
      setNotes("");
    }
  }, [open, editingStaff]);

  const isValid =
    staffName.trim().length > 0 &&
    providerCompany.trim().length > 0 &&
    providerCountry.trim().length > 0 &&
    accessLevel.trim().length > 0 &&
    startDate.length > 0;

  const buildPayload = () => ({
    staffName: staffName.trim(),
    staffRole: staffRole.trim() || undefined,
    staffEmail: staffEmail.trim() || undefined,
    providerCompany: providerCompany.trim(),
    providerCountry: providerCountry.trim(),
    accessLevel: accessLevel.trim(),
    complianceStatus,
    confidentialitySigned,
    confidentialityDate: confidentialityDate || undefined,
    privacyTrainingCompleted,
    privacyTrainingDate: privacyTrainingDate || undefined,
    startDate,
    endDate: endDate || undefined,
    notes: notes.trim() || undefined,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/compliance/offshore-staff", buildPayload());
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to create staff record");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/offshore-staff"] });
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/security-dashboard"] });
      toast({ title: "Offshore staff record created" });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingStaff?.id) throw new Error("No staff ID");
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/offshore-staff/${editingStaff.id}`,
        buildPayload()
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to update staff record");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/offshore-staff"] });
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/security-dashboard"] });
      toast({ title: "Offshore staff record updated" });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = () => {
    if (!isValid) return;
    if (isEditMode) {
      editMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="modal-offshore-staff-form"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Staff Record" : "Add Offshore Staff"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the offshore staff compliance details."
              : "Add a new offshore staff member to the compliance registry."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Personal Details */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Personal Details
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="staff-name">
                  Full Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="staff-name"
                  data-testid="input-staff-name"
                  value={staffName}
                  onChange={(e) => setStaffName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="staff-role">Role</Label>
                <Input
                  id="staff-role"
                  data-testid="input-staff-role"
                  value={staffRole}
                  onChange={(e) => setStaffRole(e.target.value)}
                  placeholder="e.g. Data Entry Operator"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="staff-email">Email</Label>
                <Input
                  id="staff-email"
                  data-testid="input-staff-email"
                  type="email"
                  value={staffEmail}
                  onChange={(e) => setStaffEmail(e.target.value)}
                  placeholder="jane@provider.com"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="provider-company">
                  Provider Company <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="provider-company"
                  data-testid="input-provider-company"
                  value={providerCompany}
                  onChange={(e) => setProviderCompany(e.target.value)}
                  placeholder="e.g. Outsource Corp Pty Ltd"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="provider-country">
                  Country <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="provider-country"
                  data-testid="input-provider-country"
                  value={providerCountry}
                  onChange={(e) => setProviderCountry(e.target.value)}
                  placeholder="e.g. Philippines"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="access-level">
                  Access Level <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="access-level"
                  data-testid="input-access-level"
                  value={accessLevel}
                  onChange={(e) => setAccessLevel(e.target.value)}
                  placeholder="e.g. Read-only CRM access"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Overall Compliance Status</Label>
                <Select value={complianceStatus} onValueChange={setComplianceStatus}>
                  <SelectTrigger data-testid="select-compliance-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPLIANCE_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Compliance */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Compliance
            </h3>

            {/* Confidentiality */}
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Confidentiality Agreement Signed</Label>
                <Switch
                  data-testid="switch-confidentiality-signed"
                  checked={confidentialitySigned}
                  onCheckedChange={setConfidentialitySigned}
                />
              </div>
              {confidentialitySigned && (
                <div className="space-y-1.5">
                  <Label htmlFor="confidentiality-date" className="text-sm">Date Signed</Label>
                  <Input
                    id="confidentiality-date"
                    data-testid="input-confidentiality-date"
                    type="date"
                    value={confidentialityDate}
                    onChange={(e) => setConfidentialityDate(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Privacy Training */}
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Privacy Training Completed</Label>
                <Switch
                  data-testid="switch-privacy-training-completed"
                  checked={privacyTrainingCompleted}
                  onCheckedChange={setPrivacyTrainingCompleted}
                />
              </div>
              {privacyTrainingCompleted && (
                <div className="space-y-1.5">
                  <Label htmlFor="privacy-training-date" className="text-sm">Date Completed</Label>
                  <Input
                    id="privacy-training-date"
                    data-testid="input-privacy-training-date"
                    type="date"
                    value={privacyTrainingDate}
                    onChange={(e) => setPrivacyTrainingDate(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Engagement Dates
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="start-date">
                  Start Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="start-date"
                  data-testid="input-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  data-testid="input-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                data-testid="input-staff-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save-offshore-staff"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending
              ? "Saving..."
              : isEditMode
              ? "Save Changes"
              : "Add Staff Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
